import type {XscpLibraryImport} from '@packages/systems/core/records/Xscp';
import _, {flatMap} from 'lodash';
import {
  List,
  Map as ImmMap,
  Set as ImmSet,
  fromJS,
  OrderedSet as ImmOrderedSet,
} from 'immutable';
import {
  PASTE,
  XSCP_ASSET_IMPORT_SUCCEEDED,
} from '@packages/systems/designer/actions';
import {maybe, None, Some} from '@packages/utilities/fp/option';

import {
  emptyArray,
  emptyObject,
  extractArray,
} from '@packages/utilities/fp/utils';
import {lookup, dissoc} from '@packages/utilities/key-map';

import {replaceAll} from '@packages/utilities/string-utils';

import {calculateAffectedForStyleBlock} from '@packages/shared/utils/AffectedNodesUtils';
import {collapseSpaces} from '@packages/systems/core/utils/TextUtils';
import {findByFileHash} from '@packages/shared/utils/AssetUtils';
import {COMBINATORS} from '@packages/systems/core/constants/Selectors';
import {
  toClassName,
  isValidClassName,
  getUserlandPrefix,
} from '@packages/systems/style-legacy/selectors';
import {getFontFamily} from '@packages/systems/style-legacy/utils';
import {swatchPlaceholder} from '@packages/systems/style-legacy/swatches';
import {weakMemo} from '@packages/utilities/memo';
import {parseVariantKey} from '@packages/systems/style-legacy/variants';
import {
  cssToPropertyList,
  cssToPropertyMap,
} from '@packages/systems/core/utils/CssStringUtils';
import {generateGuid} from '@packages/utilities/guid';
import {reportBug} from '@packages/utilities/reporter';

import type {
  StyleBlock,
  StyleBlockVariant,
} from '@packages/systems/style-legacy/records';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {SuggestionType} from './StyleRuleUtilsTypes';
import type {AssetState} from '@packages/systems/designer/assets';
import type {Font} from '@packages/systems/core/constants/Fonts';
import type {
  CssVariablesStateType,
  RawStyleBlockServerType,
} from '@packages/systems/core/utils/StyleTypes';
import type {BreakpointID} from '@packages/systems/style/types';

import {
  mergeDefaultStyleBlocks,
  importStyleBlocks,
  importStyleBlock,
  buildParentIndex,
  buildOrderList,
  buildAffectsMap,
  buildCssPropertyMap,
  updateCssPropertyMap,
  sortByDepth,
  accumulateStyleBlocks,
  decorateStyleBlock,
  getMainSelectorChunks,
  getVariantSelectorChunks,
  exportStyleBlock,
  toCssClass,
  getStylesFromStyleBlock,
  getStyleLess,
  buildStyleBlock,
  getJustCssProperties,
  createStyleBlock,
  replaceBackgroundImages,
} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';

import {
  LARGER_BREAKPOINTS,
  keyByBreakpoints,
} from '@packages/systems/style/media-queries';
import {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import {
  updateStyleRule,
  deleteStyleBlocks as deleteStyleBlockUtil,
  reifyStyleBlock,
} from '@packages/systems/style/utils';
import {Component, Plugins} from '@packages/systems/wfdl';
import {
  getXscpDataWhenPastingSerializedXscp,
  getCommittedSharedLibraryPasteImport,
  PasteAction,
} from '../../utils/platform/ElementPasteUtils';
import {XscpData} from '@packages/systems/core/records/Xscp';

import {LibrariesCapabilitiesMap} from '@packages/systems/libraries';
import type {IX3State} from '@packages/systems/ix3/store';

export {
  mergeDefaultStyleBlocks,
  importStyleBlocks,
  importStyleBlock,
  buildParentIndex,
  buildOrderList,
  buildAffectsMap,
  buildCssPropertyMap,
  decorateStyleBlock,
  getMainSelectorChunks,
  getVariantSelectorChunks,
  exportStyleBlock,
  toCssClass,
  getStylesFromStyleBlock,
  getStyleLess,
  buildStyleBlock,
  getJustCssProperties,
  createStyleBlock,
};

type StyleBlocks = ImmMap<string, StyleBlock>;

/**
 * Converts a user-entered style block name to a clean version.
 * @param  {String} name The style block name as entered by the user (e.g. on the StyleTab)
 * @return {String}      The clean version of the user-entered value.
 */
export function cleanName(name?: string | null) {
  if (!name || !_.isString(name)) {
    return '';
  }
  return collapseSpaces(
    _.trim(name.replace(/^[^a-zA-Z0-9]*/, '').replace(/[^a-zA-Z0-9\-_ ]/g, ''))
  );
}

/**
 * Generates new ids for each StyleBlock.
 * @param  {Object}  Current state.
 * @return {Array}   Array with new ids.
 */
export function generateNewIdMap(
  state: StyleBlockStateType,
  styleBlockGuid: string
) {
  const styleBlock = state.getBlock(styleBlockGuid);
  const result: Record<string, any> = {};

  if (styleBlock) {
    state.walkStyleBlockTree([styleBlock], (childStyleBlock) => {
      result[childStyleBlock.get('_id')] = generateGuid();
    });
  }

  return result;
}

/**
 * Determines the depth of a StyleBlock in the tree (e.g. 0 = global, 1 = combo block, 2 = 2nd level combo, etc)
 */
export function getStyleBlockDepth(
  parentIndex: ImmMap<string, string>,
  styleBlockId: string
) {
  let depth = 0;
  let parentGuid = parentIndex.get(styleBlockId);

  while (parentGuid) {
    depth++;
    parentGuid = parentIndex.get(parentGuid);
  }

  return depth;
}

/**
 * Return true if the styleblock has a specific property
 */
export const styleBlockHasProperty =
  (property: string) => (styleBlock: StyleBlock) =>
    // @ts-expect-error - TS7006 - Parameter 'prop' implicitly has an 'any' type.
    cssToPropertyList(styleBlock.styleLess).some(function (prop) {
      return prop.property === property;
    });

const getDepth = weakMemo(
  // @ts-expect-error - TS7006 - Parameter 'parentIndex' implicitly has an 'any' type. | TS7024 - Function implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
  (parentIndex) => (blockId: any) =>
    // _FlowFixMe - Uncovered when removing Memoized<> wrappings...
    parentIndex.has(blockId)
      ? // _FlowFixMe - getDepth expects non-undefined val in second arg, but parentIndex.get(...) can return undefined
        1 + getDepth(parentIndex)(parentIndex.get(blockId))
      : 0
);

const getAssetId = (asset: any) => asset.get('_id');
const getFamily = (font: any) => font.family;
const lookupFontFamily = lookup('font-family');
const removeFontFamily = dissoc('font-family');

const keepFamilyIfMissing = weakMemo(
  // @ts-expect-error - TS7006 - Parameter 'fontFamilyList' implicitly has an 'any' type.
  (fontFamilyList) => (family: any) =>
    fontFamilyList.includes(family) ? None : Some(family)
);

export const toStyleLess = (cssPropertyMap: {[key: string]: any}) =>
  Object.keys(cssPropertyMap)
    .map((prop) => prop + ': ' + cssPropertyMap[prop] + ';')
    .join(' ');

// @ts-expect-error - TS2322 - Type '(fontFamilyList: string[]) => (styleLess: string) => (string | any[])[]' is not assignable to type '(arg1: string[]) => (arg1: string) => [string[], string]'.
const removeMissingFontFromStyleLess: (
  arg1: Array<string>
) => (arg1: string) => [Array<string>, string] = weakMemo(
  (fontFamilyList) => (styleLess) => {
    const cssPropertyMap = cssToPropertyMap(styleLess);
    return maybe([emptyArray, styleLess])((family) => [
      [family],
      toStyleLess(removeFontFamily(cssPropertyMap)),
    ])(
      lookupFontFamily(cssPropertyMap)
        .map(getFontFamily)
        .chain(keepFamilyIfMissing(fontFamilyList))
    );
  }
);

const stripMissingFonts =
  (fontList: string[]) =>
  (styleBlock: StyleBlock): [string[], StyleBlock] => {
    const [variantDroppedFonts, variants] = styleBlock.variants.reduce(
      ([allDropped, allVariants]: [any, any], variant, variantKey) => {
        const [dropped, variantStyleLess] = removeMissingFontFromStyleLess(
          fontList
        )(variant.get('styleLess') || '');
        return [
          allDropped.concat(dropped),
          allVariants.set(
            variantKey,
            variant.set('styleLess', variantStyleLess)
          ),
        ];
      },
      [emptyArray, styleBlock.variants]
    );

    const [droppedFonts, styleLess] = removeMissingFontFromStyleLess(fontList)(
      styleBlock.get('styleLess')
    );
    return [
      droppedFonts.concat(variantDroppedFonts),
      styleBlock.set('styleLess', styleLess).set('variants', variants),
    ];
  };

const getFontList = (action: any) =>
  (action.type === PASTE && action.payload.fontList) ||
  (action.type === XSCP_ASSET_IMPORT_SUCCEEDED &&
    action.meta.get('fontList').toJS()) ||
  emptyArray;

const getInitialId = (action: any) =>
  (action.type === PASTE && action.payload.initialStyleBlockId) ||
  (action.type === XSCP_ASSET_IMPORT_SUCCEEDED &&
    action.meta.get('initialStyleBlockId')) ||
  '';

const updateBreakpointsFromNodeStyles = (
  state: StyleBlockStateType,
  actionData: any
): StyleBlockStateType => {
  const currentBreakpointsIds = Object.keys(state.breakpoints);
  if (LARGER_BREAKPOINTS.length === currentBreakpointsIds.length) {
    return state;
  }

  const newBreakpointIds: Array<any | string> = [];
  const isNewBreakpointId = (key: BreakpointID) =>
    !state.breakpoints[key] && !newBreakpointIds.includes(key);

  // @ts-expect-error - TS7006 - Parameter 'xscpData' implicitly has an 'any' type.
  actionData.map((xscpData) => {
    let i = 0;

    while (
      i < xscpData.payload.nodes.length &&
      newBreakpointIds.length + currentBreakpointsIds.length <
        LARGER_BREAKPOINTS.length
    ) {
      const node = xscpData.payload.nodes[i];
      const localStyles = _.get(node, 'data.style.base', {});
      for (const variantKey in localStyles) {
        const {mediaQuery} = parseVariantKey(variantKey);
        if (isNewBreakpointId(mediaQuery)) {
          newBreakpointIds.push(mediaQuery);
        }
      }

      i++;
    }
  });

  if (!newBreakpointIds.length) return state;

  const newBreakpoints = LARGER_BREAKPOINTS.filter(
    ({id}) => state.breakpoints[id] || newBreakpointIds.includes(id)
  );

  return state.set('breakpoints', keyByBreakpoints(newBreakpoints));
};

export const importXscpStyles = weakMemo((action: PasteAction) => {
  const xscpDataImm = getXscpDataWhenPastingSerializedXscp(action);
  const styleBlockState = maybe(action.state.StyleBlockStore)(
    (libraryImport: XscpLibraryImport) => {
      const state = action.state.StyleBlockStore;
      const {styles} = libraryImport;
      return state
        .merge({
          styleBlocks: state.styleBlocks.merge(styles.styleBlocks),
          parentIndex: state.parentIndex.merge(styles.parentIndex),
          globalOrder: state.globalOrder.union(styles.globalOrder),
          styleBlockIds: state.styleBlockIds.union(styles.styleBlockIds),
          cssPropertyMap: state.cssPropertyMap.merge(styles.cssPropertyMap),
        } satisfies XscpLibraryImport['styles'])
        .resetAddedRule()
        .resetChangedRule();
    }
  )(getCommittedSharedLibraryPasteImport(action));

  const data = updateCssPropertyMap(
    extractArray(
      xscpDataImm.map((xscpData: XscpData) => {
        const assetMap = (
          action.type === PASTE
            ? ImmMap()
            : (action.payload.response.get('importedFiles') ?? ImmMap()).map(
                getAssetId
              )
        ).merge(
          flatMap(xscpData.payload.assets, (asset) => {
            const existing = findByFileHash(asset)(
              action.state.AssetStore.assets
            );
            return existing ? [[asset._id, existing._id]] : emptyArray;
          })
        );

        return List(xscpData.payload.styles)
          .map(buildStyleBlock)
          .map(replaceBackgroundImages(assetMap))
          .update(sortByDepth)
          .map(stripMissingFonts(getFontList(action).map(getFamily)))
          .toArray();
      })
    ).reduce(accumulateStyleBlocks, {
      // @ts-expect-error `payload.importStrategy` can be undefined even during PASTE, and is not expected at all during XSCP_ASSET_IMPORT_SUCCEEDED
      importStrategy: action.payload.importStrategy,
      state: styleBlockState,
      enforceUniqueComboClasses: false,
      renames: emptyArray,
      droppedFonts: emptyArray,
      nextId: getInitialId(action),
      idMap: emptyObject,
      parentIdMap: emptyObject,
      slugMap: Plugins.getPluginsSlugMap(action.state.DesignerStore.plugins),
    })
  );

  return {
    ...data,
    state: updateBreakpointsFromNodeStyles(data.state, xscpDataImm),
  };
});

export const getSuggestionApplyResult = ({
  styleBlockState,
  suggestion,
  currentStyleBlockGuid,
  currentPageId,
  newStyleBlockId,
  isBranchMode,
  createdBy,
}: {
  styleBlockState: StyleBlockStateType;
  suggestion: SuggestionType;
  currentStyleBlockGuid: string | null;
  currentPageId: string | null;
  newStyleBlockId: string;
  isBranchMode: boolean;
  createdBy: string | null | undefined;
}) => {
  const parentStyleBlockGuid =
    currentStyleBlockGuid &&
    suggestion.type !== 'element' &&
    // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2554 - Expected 2 arguments, but got 1.
    styleBlockState.getBlock(currentStyleBlockGuid).get('type') === 'class'
      ? currentStyleBlockGuid
      : null;

  const styleBlock = suggestion.create
    ? buildStyleBlock({
        _id: newStyleBlockId,
        type: suggestion.type === 'element' ? 'element' : 'class',
        name: suggestion.name,
        comb: parentStyleBlockGuid ? COMBINATORS.COMBO : COMBINATORS.NONE,
        createdBy,
      })
    : styleBlockState.getBlock(suggestion._id);

  const updatedStyleBlocks = (() => {
    // @ts-expect-error - TS18049 - 'styleBlock' is possibly 'null' or 'undefined'.
    const styleBlockType = styleBlock.get('type');
    if (styleBlockType === 'class' || styleBlockType === 'element') {
      return createStyleBlock(styleBlockState, {
        // @ts-expect-error - TS2322 - Type 'StyleBlock | null | undefined' is not assignable to type 'StyleBlock'.
        styleBlock,
        parentGuid: parentStyleBlockGuid,
      });
    } else {
      // @ts-expect-error - TS18049 - 'styleBlock' is possibly 'null' or 'undefined'.
      const styleBlockGuid = styleBlock.get('_id');
      let state = styleBlockState;
      state = reifyStyleBlock(
        state,
        {
          _id: styleBlockGuid,
        },
        isBranchMode
      );
      state = updateOneAffectsCount(state, {
        blockGuid: styleBlockGuid,
        currentPageId,
        nodeCount: 0,
      });
      return state;
    }
  })();

  return {
    updatedStyleBlocks,
    styleBlock,
  };
};

/**
 * flattenBlock: Flatten a StyleBlockRecord into an ordered array of StyleBlockRecords.
 *
 * This function is manually curried with specialized memoizers for performance.
 *
 * Usage:
 *
 * flattenBlock(params: FlattenBlockParams)(styleBlockState: StyleBlockStateType)(block: StyleBlockRecord)
 *
 * Returns: Array<StyleBlockRecord>
 */

// @ts-expect-error - TS7006 - Parameter 'params' implicitly has an 'any' type.
const flattenBlock = weakMemo((params) =>
  // @ts-expect-error - TS7006 - Parameter 'styleBlockState' implicitly has an 'any' type.
  weakMemo((styleBlockState) =>
    // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
    weakMemo((block) => {
      const {decorate = false, includeFake = false} = params;
      if (!includeFake && block.get('fake')) {
        return [];
      }
      const flattenedListOfBlocks = [block];
      styleBlockState
        .getChildren(block.get('_id'), {decorate})
        // @ts-expect-error - TS7006 - Parameter 'childBlock' implicitly has an 'any' type.
        .forEach((childBlock) => {
          flattenedListOfBlocks.push(
            ...flattenBlock(params)(styleBlockState)(childBlock)
          );
        });
      return flattenedListOfBlocks;
    })
  )
);

/**
 * Duplicates the styleblock and all it's children
 * @param  {sourceBlockId} the styleblock to duplicate
 * @return {Object[]}
 */
export function duplicateStyleBlock(
  state: StyleBlockStateType,
  {
    _id,
    newName,
    newIdMap,
    currentPageId,
    assetState,
    cssVariablesState,
    componentMap,
    plugins,
    fonts,
    createdBy,
  }: {
    _id: string;
    newName: string;
    newIdMap: Record<any, any>;
    currentPageId: string;
    componentMap: Component.ComponentMap;
    plugins: Plugins.PluginsType;
    assetState?: AssetState;
    cssVariablesState?: CssVariablesStateType;
    fonts: Font[];
    createdBy?: string | null;
  }
): StyleBlockStateType {
  if (!newName) {
    return state;
  }

  const originalStyleBlockId = _id;
  const originalStyleBlock = state.getBlock(originalStyleBlockId);
  if (!originalStyleBlock) {
    return state;
  }

  const duplicatedRootBlockId = newIdMap[originalStyleBlockId];
  const isNestedBlock = Boolean(state.getParent(originalStyleBlockId));

  // List with the new blocks created
  const duplicateBlocksWithNewIds: Array<RawStyleBlockServerType> = [];

  // Walks the stylblock being duplicated in order to create the new one
  state.walkStyleBlockTree([originalStyleBlock], (childStyleBlock) => {
    const affectedIds = calculateAffectedForStyleBlock({
      componentMap,
      plugins,
      styleBlockState: state,
      styleBlockId: childStyleBlock._id,
    });

    const exportedBlock = exportStyleBlock(childStyleBlock, {
      affectsMap: state.affectsMap.setIn(
        [childStyleBlock._id, currentPageId],
        affectedIds
      ),
      changedRule: state.changedRule,
      // @ts-expect-error - TS2345 - Argument of type '{ affectsMap: Map<string, Map<string, number>>; changedRule: Immutable.Record<ChangedRuleShape> & Readonly<ChangedRuleShape>; assetState: AssetState | undefined; cssVariablesState: CssVariablesStateType | undefined; fonts: Font[]; }' is not assignable to parameter of type '{ affectsMap: Map<string, Record<any, any>>; changedRule: ChangedRuleType; }'.
      assetState,
      cssVariablesState,
      fonts,
    });
    exportedBlock._id = newIdMap[exportedBlock._id];
    exportedBlock.data.name =
      originalStyleBlock === childStyleBlock
        ? newName
        : exportedBlock.data.name;
    exportedBlock.data.createdBy = createdBy;

    if (exportedBlock.data.children) {
      exportedBlock.data.children = exportedBlock.data.children.map(
        (oldId) => newIdMap[oldId]
      );
    }

    // When duplicating styleblocks, assume that only the root block being duplicated will have
    // one usage on the current page because our UI auto-attaches it to the selected node during duplication
    // All other blocks deeper in the tree (children and grandchildren) should have their affects
    // data reset since they will not be attached to any nodes anywhere on the site
    exportedBlock.data.affects =
      originalStyleBlock === childStyleBlock ? {[currentPageId]: 1} : {};
    duplicateBlocksWithNewIds.push(exportedBlock);
  });

  // Updates current styleblocks list in order to include the duplicated styleblocks inside children list
  const originalStyleBlockList: StyleBlocks =
    originalStyleBlock.comb === COMBINATORS.COMBO
      ? state.styleBlocks.map(function (value) {
          if (value.get('children').includes(originalStyleBlockId)) {
            return value.update('children', (children: ImmOrderedSet<string>) =>
              children.concat(duplicatedRootBlockId)
            );
          }
          return value;
        })
      : state.styleBlocks;

  const reifiedDuplicateBlocks = importStyleBlocks(duplicateBlocksWithNewIds, {
    cleanChildIds: true,
    assetState,
    importErrors: [],
  });

  // Adds to the current styleblock list the duplicated styleblocks lists
  const newStyleBlockList = originalStyleBlockList.mergeDeep(
    reifiedDuplicateBlocks.reduce(
      (result: StyleBlocks, item: StyleBlock) =>
        result.set(item.get('_id'), item),
      ImmMap()
    )
  );

  // Updates parentIndex taking in count the duplicated styleblocks
  // @ts-expect-error - TS2345 - Argument of type 'Map<string, StyleBlock>' is not assignable to parameter of type 'List<StyleBlock>'.
  const partialParentIndex = buildParentIndex(newStyleBlockList);

  const affects: Record<string, any> = {};
  duplicateBlocksWithNewIds.map(function (item) {
    affects[item._id] = item.data.affects;
  });
  const partialAffectsMap = fromJS(affects);

  return state
    .resetChangedRule()
    .resetAddedRule()
    .set('styleBlocks', newStyleBlockList)

    .set('affectsMap', state.affectsMap.mergeDeep(partialAffectsMap))
    .update('globalOrder', (globalOrder) => {
      return isNestedBlock
        ? globalOrder
        : globalOrder.add(duplicatedRootBlockId);
    })
    .set(
      'cssPropertyMap',
      buildCssPropertyMap(
        // Important: `buildCssPropertyMap` expects a _List_ of styleBlocks; not a map:
        newStyleBlockList.toList()
      )
    )
    .set('parentIndex', state.parentIndex.mergeDeep(partialParentIndex))
    .update('styleBlockIds', (styleBlockIds) =>
      ImmSet([
        ...styleBlockIds,
        ...duplicateBlocksWithNewIds.map((block) => block._id),
      ])
    );
}

/**
 * Checks whether we can safely rename the given style block id to the new name (i.e. would the new name conflict with another block in the tree).
 * Make sure this returns true before doing renameStyleBlock().
 */
export function canRenameStyleBlock(
  state: StyleBlockStateType,
  slugMap: Plugins.PluginsType['slugMap'],
  {
    _id,
    newName,
    duplicateMode,
  }: {
    _id?: string;
    newName: null | string;
    duplicateMode?: boolean;
  }
) {
  if (!_id || !newName || !isValidClassName(newName)) {
    return false;
  }

  const styleBlock = state.getBlock(_id);

  if (!styleBlock || styleBlock.get('type') !== 'class') {
    return false;
  }

  const newClassName = toClassName(newName, {
    userlandPrefix: styleBlock.origin
      ? getUserlandPrefix(styleBlock.origin, slugMap)
      : null,
  });
  const upstreamStyleBlocks = state.getTreePath(_id).slice(0, -1);
  const downstreamStyleBlocks = (() => {
    const result: Array<StyleBlock | any> = [];
    state.walkStyleBlockTree([styleBlock], (instanceStyleBlock) => {
      if (instanceStyleBlock !== styleBlock) {
        result.push(instanceStyleBlock);
      }
    });
    return result;
  })();
  const globalStyleBlocks = (() => {
    if (state.parentIndex.get(styleBlock.get('_id')) && !duplicateMode) {
      return [];
      // When duplicating, we want to ensure all style blocks are considered since we're creating
      // entirely new class
    } else {
      return state
        .getGlobalStyleBlocks()
        .toArray()
        .filter((rootStyleBlock) => rootStyleBlock.get('_id') !== _id);
    }
  })();

  return !upstreamStyleBlocks
    .concat(downstreamStyleBlocks)
    .concat(globalStyleBlocks)
    .some((block) => {
      const blockType = block.get('type');

      if (blockType !== 'class') return false;

      const blockClassName = toClassName(block.get('name'), {
        userlandPrefix: block.origin
          ? getUserlandPrefix(block.origin, slugMap)
          : null,
      });
      return blockClassName === newClassName;
    });
}

/**
 * Renames a style block with no sanity checks.
 */
export function renameStyleBlock(
  state: StyleBlockStateType,
  {
    _id,
    newName,
  }: {
    _id: string;
    newName: string;
  }
): StyleBlockStateType {
  if (!isValidClassName(newName)) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('InvalidClassName', 'While renaming style block', {
      action: {
        _id,
        newName,
      },
    });

    newName = '-Error-';
  }

  return (
    state
      .resetChangedRule()
      .resetAddedRule()
      .updateIn(['styleBlocks', _id], (block) => {
        // @ts-expect-error - TS2339 - Property 'set' does not exist on type '{}'.
        return block ? block.set('name', newName) : block;
      })
      // Add _id to styleBlockIds to ensure that the styleBlock
      // is assigned to the current page or branch when saved
      .update('styleBlockIds', (styleBlockIds) => styleBlockIds.add(_id))
  );
}

/**
 * Remove multiple StyleBlocks from the style tree.
 * @param  {Array<String>}      options._ids                   The block IDs to remove.
 * @param  {PageID}             options.currentPageId         The current page ID
 * @param  {IXState}            options.ixState               The current state of IXStore
 */
export function deleteStyleBlocks(
  state: StyleBlockStateType,
  {
    _ids,
    componentMap,
    plugins,
    currentPageId,
    ixState,
    ix2State,
    ix3State,
    librariesCapabilitiesMap,
  }: {
    _ids: Array<string>;
    componentMap: Component.ComponentMap;
    plugins: Plugins.PluginsType;
    currentPageId: string;
    ixState: Record<any, any>;
    ix2State: Record<any, any>;
    ix3State: IX3State;
    librariesCapabilitiesMap: LibrariesCapabilitiesMap;
  }
): StyleBlockStateType {
  try {
    // calling the util function below instead of using the new util function directly
    // so that we can handle any errors that occur directly within this function.
    // Each call site of the `deleteStyleBlockUtil` will be responsible for its error handling.
    return deleteStyleBlockUtil(state, {
      _ids,
      componentMap,
      plugins,
      currentPageId,
      ixState,
      ix2State,
      ix3State,
      librariesCapabilitiesMap,
    });
  } catch (styleBlock: unknown) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug(
      'StyleBlockDeleteError',
      'Tried to remove a non-removable style block',
      {styleBlock}
    );
    return state;
  }
}

/**
 * Updates the number of times a StyleBlock is used on a particular page.
 * @param  {String} options.blockGuid The Block's top-level GUID.
 * @param  {String} options.pageGuid  The mongo ID of the page to update the count for.
 * @param  {Number} options.nodeCount The number of elements this block affects on this page.
 */
export function updateOneAffectsCount(
  state: StyleBlockStateType,
  {
    blockGuid,
    currentPageId,
    nodeCount,
  }: {
    blockGuid: string;
    currentPageId: string | null;
    nodeCount: number;
  }
): StyleBlockStateType {
  return state.resetAddedRule().update('affectsMap', (affectsMap) => {
    return affectsMap.setIn([blockGuid, currentPageId], nodeCount);
  });
}

/**
 * Updates the StyleBlockID -> [...ElementID] mapping for the current page.
 */
export function updateAffectsCounts(
  state: StyleBlockStateType,

  /**
   * The StyleBlock_id -> [ DomNode_id ] map as calculated by
   * `AffectedNodesUtils.recalculateAffected()`
   */
  styleMap: Record<any, any>,

  /**
   * The Mongo ID of the current page that is open in the designer.
   */
  currentPageId: string
): StyleBlockStateType {
  const newAffectsMap = state.affectsMap.map((blockAffects, blockGuid) => {
    const numberOfNewAffects = (styleMap[blockGuid] || []).length;

    // if there are no new affects, we can remove the block from the map
    if (numberOfNewAffects === 0) {
      return blockAffects.delete(currentPageId);
    }

    return blockAffects.set(currentPageId, numberOfNewAffects);
  });

  const statesAreEqual = state.affectsMap.equals(newAffectsMap);

  if (statesAreEqual) {
    return state;
  }

  return state.resetAddedRule().update('affectsMap', () => {
    return newAffectsMap;
  });
}

/**
 * Replaces a swatch placeholder in the entire store with a static color.
 * @param  {StyleBlockStateType}  state
 * @param  {String}           swatchId
 * @param  {String}           staticColor
 * @return {StyleBlockStateType}
 */
export function replaceSwatchPlaceholder(
  state: StyleBlockStateType,
  {
    swatchId,
    staticColor,
  }: {
    swatchId: string;
    staticColor: string;
  }
): StyleBlockStateType {
  const replaceSwatch = (styleLess: string) =>
    replaceAll(styleLess, swatchPlaceholder(swatchId), staticColor);
  const replaceSwatchInVariant = (variant: StyleBlockVariant) =>
    variant.update('styleLess', replaceSwatch);

  return state
    .resetChangedRule()
    .resetAddedRule()
    .update('styleBlocks', (styleBlocks) =>
      styleBlocks.map((styleBlock) => {
        return styleBlock
          .update('styleLess', replaceSwatch)
          .update('variants', (variants) => {
            return variants.map(replaceSwatchInVariant);
          });
      })
    );
}

const _3dTransformRegex =
  /translateZ|translate3d|scaleZ|scale3d|rotateX|rotateY|rotate3d|matrix3d/;
const _has3dTransform = (styleLess: string) => {
  return _3dTransformRegex.test(styleLess);
};

const needsPreserve3d = weakMemo((styleBlock: StyleBlock) => {
  return (
    _has3dTransform(styleBlock.styleLess) ||
    styleBlock.variants
      .map((variant) => variant.get('styleLess') || '')
      .some(_has3dTransform)
  );
});

export const addPreserve3dToStyleBlockIfNeeded = weakMemo(
  (styleBlock: StyleBlock): StyleBlock => {
    if (needsPreserve3d(styleBlock)) {
      return styleBlock.set(
        'styleLess',
        `${styleBlock.styleLess} transform-style: preserve-3d;`.trim()
      );
    }

    return styleBlock;
  }
);

const updateStyleBlocksWithPreserve3d = weakMemo(
  (styleBlocks: ImmMap<string, StyleBlock>) => {
    return styleBlocks.map(addPreserve3dToStyleBlockIfNeeded);
  }
);
export const addPreserve3dIfNeeded = weakMemo(
  (state: StyleBlockStateType): StyleBlockStateType =>
    state.update('styleBlocks', updateStyleBlocksWithPreserve3d)
);

const _updateStyleBlocks = weakMemo(
  function _updateStyleBlocks_memoStyleBlockState(
    styleBlockState: StyleBlockStateType
  ) {
    return weakMemo(function _updateStyleBlocks_memoStyleBlocks(
      styleBlocks: RawStyleBlockType[]
    ) {
      let __styleBlockState = styleBlockState.asMutable();

      for (const styleBlock of styleBlocks) {
        if (
          __styleBlockState.styleBlocks.get(styleBlock._id) &&
          styleBlock.styleLess
        ) {
          __styleBlockState = updateStyleRule(__styleBlockState, {
            styleBlockGuid: styleBlock._id,
            newStyles: styleBlock.styleLess,
          });
          continue;
        }

        __styleBlockState = createStyleBlock(__styleBlockState, {
          styleBlock: buildStyleBlock(styleBlock),
          parentGuid: undefined,
        });
      }

      return __styleBlockState.asImmutable();
    });
  }
);

/**
 * Updates the StyleBlockState with the new style blocks.
 * @param styleBlockState State
 * @param styleBlocks Raw style blocks
 * @returns styleBlockState
 */
export function updateStyleBlocks(
  styleBlockState: StyleBlockStateType,
  styleBlocks: RawStyleBlockType[] = emptyArray
): StyleBlockStateType {
  return _updateStyleBlocks(styleBlockState)(styleBlocks);
}
